/**
 * MarketingInitiativeFormV2 — redesigned Marketing Initiative form.
 *
 * Sections (same fields as v1):
 *   01 Basic info       — name, code (uppercase alphanumeric only),
 *                         description, default unlock duration,
 *                         optional global_campaign_lifespan
 *   02 Resources        — pick a resource type (Challenge / Series /
 *                         Session / Quick Tap / Card Deck), then pick
 *                         a resource from that type, accumulate into
 *                         a mini-table. Required: at least one.
 *   03 Transaction log  — collapsible RailCard at the bottom (edit
 *                         only, only if logs exist).
 *   04 Danger zone      — delete the initiative (edit only).
 *
 * Same Redux + apiV2 plumbing as v1 — `createMarketingInitiative` /
 * `updateMarketingInitiative` / `deleteMarketingInitiative` /
 * `fetchInitiativeLogs`. The page chrome (header + Save / Discard
 * buttons) is in the parent page wrapper, but the form's submit
 * button hooks via `form="marketing-initiative-form"` + type="submit".
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  createMarketingInitiative,
  updateMarketingInitiative,
  deleteMarketingInitiative,
} from '../../../../actions/marketingInitiativeActions';
import { apiV2 } from '../../../../actions';
import { fetchInitiativeLogs } from '../../../../services/marketingInitiatives';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import { preventEnterFormSubmission } from '../../../../utils/formUtils';
import { Button, IconButton, cn } from '../../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  Select,
  RailCard,
  RailCardHead,
} from '../../../redesign/edit';

/* ─── Icons ────────────────────────────────────────────────────────── */

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

const SearchIconSvg = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16 }}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/* ─── Constants ────────────────────────────────────────────────────── */

const RESOURCE_TYPES = [
  { code: 'challenge',         name: 'Challenge',        path: '/challenges',         id: 'challenge_id', label: 'challenge_title' },
  { code: 'series',            name: 'Series',           path: '/series',             id: 'series_id',    label: 'series_title' },
  { code: 'session',           name: 'Session',          path: '/sessions',           id: 'session_id',   label: 'session_name' },
  { code: 'quick-tap-session', name: 'Quick Tap Session', path: '/quick-tap-sessions', id: 'id',           label: 'title' },
  { code: 'deck',              name: 'Card Deck',        path: '/decks',              id: 'id',           label: 'name' },
];

const NAME_VALID = /^[a-zA-Z0-9\s\-:]+$/;

/* ─── ResourceSelectButton — v2 button + dropdown. Mirrors the
 *  AuthorSelectButton recipe but in "add to list" mode: the trigger
 *  never holds a value, picks fire `onAdd` and close, already-added
 *  rows show greyed with an "added" tag. */

function ResourceSelectButton({
  typeLabel,         // "Challenge" / "Series" / …
  options,           // [{ id, name, type, key }, …]
  loading,
  disabled,          // true when no type is picked OR externally disabled
  selectedKeys,      // Set<string> of already-added resource keys
  onAdd,
  hasError,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  // Close dropdown on outside-click + Escape, same as AuthorSelectButton.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    if (!Array.isArray(options)) return [];
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      (o.name || '').toLowerCase().includes(q),
    );
  }, [options, query]);

  const placeholder =
    disabled ? 'Pick a type first' :
    loading  ? `Loading ${typeLabel.toLowerCase()}s…` :
    `Pick a ${typeLabel.toLowerCase()}…`;

  return (
    <div
      ref={wrapperRef}
      className="relative"
      style={{ minWidth: 0 }}
    >
      <button
        type="button"
        onClick={() => !disabled && !loading && setOpen((v) => !v)}
        disabled={disabled || loading}
        className={cn(
          'flex items-center cursor-pointer w-full',
          'transition-all duration-150',
          (disabled || loading) && 'cursor-not-allowed',
        )}
        style={{
          // Match TextInput / Select padding + font so this trigger
          // is the same vertical size as its sibling Resource type
          // select in the FieldRow.
          gap: 10,
          padding: '9px 12px',
          background: disabled ? '#F5EEDF' : '#FAF4EA',
          border: `1px solid ${hasError ? '#E58A6A' : '#E8E0CF'}`,
          borderRadius: 8,
          textAlign: 'left',
          fontFamily: 'inherit',
          fontSize: 13.5,
          opacity: disabled || loading ? 0.6 : 1,
        }}
        onMouseEnter={(e) => {
          if (disabled || loading) return;
          e.currentTarget.style.borderColor = '#75A293';
          e.currentTarget.style.background = '#FAF7F1';
        }}
        onMouseLeave={(e) => {
          if (disabled || loading) return;
          e.currentTarget.style.borderColor = hasError ? '#E58A6A' : '#E8E0CF';
          e.currentTarget.style.background = '#FAF4EA';
        }}
      >
        <span
          aria-hidden
          className="grid place-items-center text-text-muted shrink-0"
          style={{ width: 16, height: 16 }}
        >
          {SearchIconSvg}
        </span>
        <span
          className="flex-1 min-w-0 text-text-muted italic truncate"
          style={{ fontSize: 13.5, textAlign: 'left' }}
        >
          {placeholder}
        </span>
        <span className="text-text-muted" style={{ fontSize: 11, flexShrink: 0 }}>
          ▾
        </span>
      </button>

      {open && (
        <div
          className="absolute"
          style={{
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            background: '#FAF7F1',
            border: '1px solid #E8E0CF',
            borderRadius: 8,
            boxShadow: '0 8px 24px #2C3E5018',
            overflow: 'hidden',
            zIndex: 30,
          }}
        >
          {/* Search header */}
          <div
            className="flex items-center"
            style={{
              gap: 8,
              padding: '10px 12px',
              borderBottom: '1px solid #EFE7D8',
              background: '#FAF4EA',
            }}
          >
            <span
              aria-hidden
              className="grid place-items-center text-text-muted shrink-0"
              style={{ width: 16, height: 16 }}
            >
              {SearchIconSvg}
            </span>
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${typeLabel.toLowerCase()}s by name…`}
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                background: 'transparent',
                outline: 'none',
                fontFamily: 'inherit',
                fontSize: 13,
                color: '#2C3E50',
              }}
            />
          </div>

          {/* Options list */}
          <div
            style={{
              maxHeight: 320,
              overflowY: 'auto',
              padding: '6px 0',
            }}
          >
            {loading ? (
              <div
                className="text-text-muted italic"
                style={{ padding: '12px 14px', fontSize: 13 }}
              >
                Loading {typeLabel.toLowerCase()}s…
              </div>
            ) : filtered.length === 0 ? (
              <div
                className="text-text-muted italic"
                style={{ padding: '12px 14px', fontSize: 13 }}
              >
                {query
                  ? `No ${typeLabel.toLowerCase()}s match "${query}".`
                  : `No ${typeLabel.toLowerCase()}s available.`}
              </div>
            ) : (
              filtered.map((o) => {
                const isAdded = selectedKeys.has(o.key);
                return (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => {
                      if (isAdded) return;
                      onAdd?.(o);
                      setOpen(false);
                    }}
                    disabled={isAdded}
                    className={cn(
                      'w-full flex items-center',
                      isAdded ? 'cursor-not-allowed' : 'cursor-pointer',
                    )}
                    style={{
                      gap: 10,
                      padding: '8px 14px',
                      fontSize: 13,
                      color: isAdded ? '#9BA4AD' : '#2C3E50',
                      background: 'transparent',
                      border: 0,
                      textAlign: 'left',
                      fontFamily: 'inherit',
                      transition: 'background 120ms ease',
                    }}
                    onMouseEnter={(e) => {
                      if (isAdded) return;
                      e.currentTarget.style.background = '#FAF4EA';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span
                      className="flex-1 min-w-0 truncate"
                      style={{ fontWeight: 600 }}
                      title={o.name}
                    >
                      {o.name}
                    </span>
                    {isAdded && (
                      <span
                        className="font-condensed font-bold uppercase text-text-muted shrink-0"
                        style={{
                          fontSize: 9.5,
                          letterSpacing: '0.08em',
                          padding: '1px 6px',
                          borderRadius: 999,
                          background: '#E8E0CF',
                        }}
                      >
                        Added
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── ResourcePicker — type select + v2 resource picker, fetches
 *  options whenever the type changes. */

function ResourcePicker({ disabled, selectedResources, onAdd, hasError }) {
  const [type, setType] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!type) {
      setOptions([]);
      return;
    }
    const cfg = RESOURCE_TYPES.find((r) => r.code === type);
    if (!cfg) return;
    let cancelled = false;
    setLoading(true);
    apiV2
      .get(cfg.path)
      .then((res) => {
        if (cancelled) return;
        const items = res.data.objects || res.data || [];
        setOptions(
          items.map((it) => ({
            id: it[cfg.id],
            name: it[cfg.label],
            type: cfg.code,
            key: `${cfg.code}-${it[cfg.id]}`,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) {
          Swal.fire({ icon: 'error', title: `Error fetching ${cfg.name.toLowerCase()}s` });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type]);

  const typeLabel = RESOURCE_TYPES.find((r) => r.code === type)?.name || 'Resource';
  const selectedKeys = useMemo(
    () => new Set(selectedResources.map((r) => r.key)),
    [selectedResources],
  );

  return (
    <FieldRow>
      <FieldBlock label="Resource type" htmlFor="f-mi-resource-type" noMargin>
        <Select
          id="f-mi-resource-type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          disabled={disabled || loading}
        >
          <option value="">Select a type…</option>
          {RESOURCE_TYPES.map((rt) => (
            <option key={rt.code} value={rt.code}>{rt.name}</option>
          ))}
        </Select>
      </FieldBlock>
      <FieldBlock
        label="Resource"
        hint="Pick a resource — it's added on selection."
        noMargin
      >
        <ResourceSelectButton
          typeLabel={typeLabel}
          options={options}
          loading={loading}
          disabled={disabled || !type}
          selectedKeys={selectedKeys}
          onAdd={onAdd}
          hasError={hasError}
        />
      </FieldBlock>
    </FieldRow>
  );
}

/* ─── Selected-resources mini-table ────────────────────────────────── */

function ResourceList({ resources, onRemove, error }) {
  if (!resources.length) {
    return (
      <div
        className={cn(
          'bg-surface border italic text-center',
          error ? 'border-coral text-coral' : 'border-sand text-text-muted',
        )}
        style={{
          borderRadius: 8,
          padding: '12px 14px',
          fontSize: 13,
          marginTop: 14,
        }}
      >
        No resources added — pick a type above to start.
      </div>
    );
  }
  return (
    <div
      className="bg-surface border border-sand overflow-hidden"
      style={{ borderRadius: 8, marginTop: 14 }}
    >
      {resources.map((r, idx) => (
        <div
          key={r.key}
          className={cn(
            'group flex items-center',
            idx > 0 && 'border-t border-line',
          )}
          style={{ padding: '8px 12px' }}
        >
          <span
            className="font-condensed font-bold uppercase shrink-0"
            style={{
              fontSize: 10,
              letterSpacing: '0.08em',
              padding: '2px 7px',
              borderRadius: 999,
              background: '#75A29318',
              color: '#5C7E72',
              marginRight: 10,
            }}
          >
            {r.type}
          </span>
          <span
            className="font-semibold text-navy truncate flex-1 min-w-0"
            style={{ fontSize: 13.5 }}
            title={r.name}
          >
            {r.name}
          </span>
          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            <IconButton
              size="sm"
              aria-label={`Remove ${r.name}`}
              title="Remove"
              onClick={() => onRemove(r)}
              type="button"
            >
              {TrashIconSvg}
            </IconButton>
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────── */

function formatLogAction(action) {
  if (!action) return '';
  return action
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/* ─── Main ─────────────────────────────────────────────────────────── */

const MarketingInitiativeFormV2 = forwardRef(function MarketingInitiativeFormV2(
  { id, onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});

  const [initiative, setInitiative] = useState({
    name: '',
    code: '',
    default_unlock_duration: '',
    description: '',
    global_campaign_lifespan: '',
    resources: [],
  });

  const [auditLogs, setAuditLogs] = useState([]);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  /* ─── Fetch on mount when editing ──────────────────────────────── */
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiV2
      .get(`/marketing-initiatives/${id}`)
      .then((res) => {
        const data = res.data;
        setInitiative({
          ...data,
          resources: (data.resources || []).map((r) => ({
            id: r.id,
            name: r.name,
            type: r.type,
            key: `${r.type}-${r.id}`,
          })),
        });
        if (onLoadedRef.current) {
          onLoadedRef.current({
            initiative_name: data.name || null,
            initiative_updated_at: data.updated_at || null,
          });
        }
      })
      .catch(() => {
        Swal.fire({ icon: 'error', title: 'Failed to load initiative' });
      })
      .finally(() => setLoading(false));
  }, [id]);

  /* ─── Audit logs ────────────────────────────────────────────────── */
  useEffect(() => {
    if (!initiative.id) return;
    (async () => {
      try {
        const logs = await fetchInitiativeLogs(initiative.id);
        setAuditLogs(logs || []);
      } catch {
        /* non-blocking */
      }
    })();
  }, [initiative.id]);

  /* ─── Handlers ─────────────────────────────────────────────────── */

  const handleChange = (e) => {
    let value = e.target.value;
    if (e.target.name === 'code') {
      // Match v1: alphanumeric uppercase only.
      value = value.replace(/[^a-z0-9]/gi, '').toUpperCase();
    }
    setInitiative((prev) => ({ ...prev, [e.target.name]: value }));
  };

  const handleAddResource = (resource) => {
    setInitiative((prev) => {
      if (prev.resources.some((r) => r.key === resource.key)) return prev;
      return { ...prev, resources: [...prev.resources, resource] };
    });
  };

  const handleRemoveResource = (resource) => {
    setInitiative((prev) => ({
      ...prev,
      resources: prev.resources.filter((r) => r.key !== resource.key),
    }));
  };

  const submit = useCallback(() => {
    const next = {};
    if (!initiative.name || initiative.name.trim().length < 3) next.name = true;
    if (!initiative.code || initiative.code.trim().length < 3) next.code = true;
    if (
      !initiative.default_unlock_duration ||
      parseInt(initiative.default_unlock_duration, 10) < 0
    ) {
      next.default_unlock_duration = true;
    }
    if (initiative.resources.length === 0) next.resources = true;

    if (Object.keys(next).length) {
      setError(next);
      Swal.fire({ icon: 'error', title: 'Please fill all the required fields' });
      return;
    }

    if (!NAME_VALID.test(initiative.name)) {
      setError({ ...next, name: true });
      Swal.fire({
        icon: 'error',
        title: 'The title must contain only alphanumeric, space and dash characters',
      });
      return;
    }

    setError({});

    const payload = {
      name: initiative.name,
      code: initiative.code,
      description: initiative.description,
      default_unlock_duration: parseInt(initiative.default_unlock_duration, 10) || null,
      global_campaign_lifespan: parseInt(initiative.global_campaign_lifespan, 10) || null,
      resources: initiative.resources.map((r) => ({ id: r.id, type: r.type })),
    };

    if (initiative.id) {
      dispatch(
        updateMarketingInitiative(initiative.id, payload, () =>
          navigate('/marketing-initiatives'),
        ),
      );
    } else {
      dispatch(
        createMarketingInitiative(payload, () => navigate('/marketing-initiatives')),
      );
    }
  }, [initiative, dispatch, navigate]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    submit();
  };

  useImperativeHandle(ref, () => ({ submit }), [submit]);

  /* ─── Delete (edit-only) ──────────────────────────────────────── */
  const handleDelete = () => {
    if (!initiative.id) return;
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          deleteMarketingInitiative(initiative.id, () =>
            navigate('/marketing-initiatives'),
          ),
        );
      }
    });
  };

  const isEdit = !!initiative.id;

  return (
    <>
      <LoadingBackdrop open={loading} />
      <form
        id="marketing-initiative-form"
        onSubmit={handleSubmit}
        onKeyDown={preventEnterFormSubmission}
        autoComplete="off"
        style={{ minWidth: 0, maxWidth: 960 }}
      >
        {/* 01 Basic info */}
        <FormSection title="Basic info" num="01">
          <FieldBlock
            label="Title"
            required
            hint="e.g. Sleep Support Challenge — Facebook Ads"
            htmlFor="f-mi-name"
          >
            <TextInput
              id="f-mi-name"
              name="name"
              value={initiative.name ?? ''}
              onChange={handleChange}
              autoFocus
              required
              hasError={!!error.name}
            />
          </FieldBlock>

          <FieldRow>
            <FieldBlock
              label="Code"
              required
              hint="Unique identifier — alphanumeric, no spaces. Stored uppercase."
              htmlFor="f-mi-code"
              noMargin
            >
              <TextInput
                id="f-mi-code"
                name="code"
                value={initiative.code ?? ''}
                onChange={handleChange}
                required
                hasError={!!error.code}
              />
            </FieldBlock>
            <FieldBlock
              label="Content unlock duration (days)"
              required
              hint="Days the content stays unlocked after redemption. Not retroactive."
              htmlFor="f-mi-unlock"
              noMargin
            >
              <TextInput
                id="f-mi-unlock"
                name="default_unlock_duration"
                type="number"
                min={0}
                value={initiative.default_unlock_duration ?? ''}
                onChange={handleChange}
                required
                hasError={!!error.default_unlock_duration}
              />
            </FieldBlock>
          </FieldRow>

          <FieldBlock
            label="Description"
            hint="Internal context only — not shown to users."
            htmlFor="f-mi-description"
          >
            <Textarea
              id="f-mi-description"
              name="description"
              value={initiative.description ?? ''}
              onChange={handleChange}
              rows={3}
            />
          </FieldBlock>

          <FieldBlock
            label="Global campaign lifespan (days)"
            hint="Optional — when set, the initiative auto-expires this many days after creation."
            htmlFor="f-mi-lifespan"
            noMargin
          >
            <TextInput
              id="f-mi-lifespan"
              name="global_campaign_lifespan"
              type="number"
              min={0}
              value={initiative.global_campaign_lifespan ?? ''}
              onChange={handleChange}
            />
          </FieldBlock>
        </FormSection>

        {/* 02 Resources */}
        <FormSection
          title="Resources"
          num="02"
          right={
            <span className="text-text-muted" style={{ fontSize: 11.5 }}>
              {initiative.resources.length} attached · required
            </span>
          }
        >
          <ResourcePicker
            disabled={loading}
            selectedResources={initiative.resources}
            onAdd={handleAddResource}
            hasError={!!error.resources}
          />
          <ResourceList
            resources={initiative.resources}
            onRemove={handleRemoveResource}
            error={!!error.resources}
          />
        </FormSection>

        {/* 03 Transaction history (edit-only, only if logs exist) */}
        {isEdit && auditLogs && auditLogs.length > 0 && (
          <FormSection
            title="Transaction history"
            num="03"
            right={
              <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                {auditLogs.length} {auditLogs.length === 1 ? 'entry' : 'entries'}
              </span>
            }
          >
            <RailCard
              head={({ open, toggle }) => (
                <RailCardHead
                  eyebrow="Audit log"
                  open={open}
                  onToggle={toggle}
                />
              )}
            >
              <div
                className="bg-surface overflow-hidden"
                style={{ borderRadius: 8 }}
              >
                {auditLogs.map((log, idx) => (
                  <div
                    key={log.id ?? idx}
                    className={cn('flex items-center', idx > 0 && 'border-t border-line')}
                    style={{ padding: '10px 14px', gap: 12 }}
                  >
                    <span
                      className="font-mono text-text-muted shrink-0"
                      style={{ fontSize: 11.5, width: 160 }}
                    >
                      {log.timestamp}
                    </span>
                    <span
                      className="text-navy truncate"
                      style={{ fontSize: 12.5, width: 200 }}
                      title={log.requested_by || ''}
                    >
                      {log.requested_by || '—'}
                    </span>
                    <span
                      className="text-text-muted"
                      style={{ fontSize: 12.5 }}
                    >
                      {formatLogAction(log.metadata?.action)}
                    </span>
                  </div>
                ))}
              </div>
            </RailCard>
          </FormSection>
        )}

        {/* 04 Danger zone (edit-only) */}
        {isEdit && (
          <FormSection title="Danger zone" num={isEdit && auditLogs?.length > 0 ? '04' : '03'}>
            <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Button variant="danger" onClick={handleDelete} type="button">
                Delete initiative
              </Button>
              <span className="text-text-muted" style={{ fontSize: 12 }}>
                Permanent — existing redemptions stay, but the code stops granting access.
              </span>
            </div>
          </FormSection>
        )}
      </form>
    </>
  );
});

export default MarketingInitiativeFormV2;
